import { joinWindowClosesMs } from '@unify/fhir-model/resources/appointment';

/**
 * Joining closes 60 minutes after the scheduled end, or 2 hours after start when there is no
 * end — matching the `client-wait` cutoff the bot enforces server-side, so the portal doesn't
 * hide a session the bot would still let the client rejoin.
 */
export function telehealthSessionHasFinished(
  startIso: string,
  endIso: string | undefined,
  nowMs = Date.now(),
): boolean {
  const startMs = Date.parse(startIso);
  if (!Number.isFinite(startMs)) return false;
  return nowMs > joinWindowClosesMs({ end: endIso }, startMs);
}

/** The chime-telehealth bot refuses client joins earlier than this before the scheduled start. */
export const CHIME_JOIN_OPENS_MINUTES = 15;

export function chimeJoinNotYetOpen(startIso: string, nowMs = Date.now()): boolean {
  const startMs = Date.parse(startIso);
  return Number.isFinite(startMs) && startMs - nowMs > CHIME_JOIN_OPENS_MINUTES * 60_000;
}

const WAITING_ROOM_OPENS_AT = /waiting room opens at (\d{4}-\d{2}-\d{2}T[\d:.]+Z)/i;

/**
 * The bots' too-early refusal names the opening instant in UTC (they cannot know the viewer's
 * zone). Rewrite it as "The waiting room will open at 8:45 AM MDT. Please join back at that time."
 * in this browser's time zone, adding the date when it is not today. Other messages pass through.
 */
export function localizeWaitingRoomOpensMessage(message: string, now = new Date()): string {
  const match = WAITING_ROOM_OPENS_AT.exec(message);
  const opensAt = match ? new Date(match[1]) : null;
  if (!opensAt || Number.isNaN(opensAt.getTime())) return message;
  const time = opensAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  const sameDay = opensAt.toDateString() === now.toDateString();
  const when = sameDay
    ? time
    : `${time} on ${opensAt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`;
  return `The waiting room will open at ${when}. Please join back at that time.`;
}
